import type { SpeechErrorCode, SpeechInputEvents, SpeechInputProvider, SpeechInputSession } from "./speech.types";

/** The slice of the Web Speech API used here. This is the only file that touches it. */
interface RecognitionResultAlternative {
  transcript: string;
}
interface RecognitionResult {
  isFinal: boolean;
  0: RecognitionResultAlternative;
  length: number;
}
interface RecognitionEvent {
  resultIndex: number;
  results: { length: number; [index: number]: RecognitionResult };
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type RecognitionConstructor = new () => Recognition;

const getConstructor = (): RecognitionConstructor | null => {
  if (typeof window === "undefined") return null;
  const scope = window as unknown as { SpeechRecognition?: RecognitionConstructor; webkitSpeechRecognition?: RecognitionConstructor };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
};

/** `REACT_APP_VOICE_INPUT=off` turns voice input off for the whole app. */
export const isVoiceInputSwitchedOff = (): boolean => process.env.REACT_APP_VOICE_INPUT === "off";

export const toSpeechErrorCode = (error: string): SpeechErrorCode => {
  switch (error) {
    case "not-allowed":
    case "service-not-allowed":
      return "not-allowed";
    case "no-speech":
    case "network":
    case "aborted":
      return error;
    default:
      return "unknown";
  }
};

/** Speech to text through the browser's own recognizer (in Chrome the browser vendor's service transcribes). */
export class BrowserSpeechProvider implements SpeechInputProvider {
  readonly id = "browser";

  isSupported = (): boolean => !isVoiceInputSwitchedOff() && getConstructor() !== null;

  start = ({ lang }: { lang: string }, events: SpeechInputEvents): SpeechInputSession => {
    const Ctor = getConstructor();
    if (!Ctor || isVoiceInputSwitchedOff()) {
      events.onError("unsupported");
      events.onEnd();
      return { stop: () => undefined, abort: () => undefined };
    }
    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let partial = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? "";
        if (result.isFinal) events.onFinal(text.trim());
        else partial += text;
      }
      // Always sent, so a finished phrase clears the partial line.
      events.onPartial(partial.trim());
    };
    recognition.onerror = (event) => events.onError(toSpeechErrorCode(event.error));
    recognition.onend = () => events.onEnd();
    try {
      recognition.start();
    } catch {
      events.onError("unknown");
      events.onEnd();
    }
    return { stop: () => recognition.stop(), abort: () => recognition.abort() };
  };
}
